import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';

interface Class {
  id: number;
  name: string;
  isSoundEnabled?: boolean;
  isAttenuationEnabled?: boolean;
  lockScreenPassword?: string | null;
  parentShareEnabled?: boolean;
  parentShareToken?: string | null;
  allowMultipleGroups?: boolean;
  students?: { id: number }[];
  studentCount?: number;
}

interface ClassState {
  currentClass: Class | null;
  setCurrentClass: (cls: Class | null) => void;
}

const safeLocalStorage = {
  getItem: (name: string) => {
    try {
      return localStorage.getItem(name);
    } catch {
      return null;
    }
  },
  setItem: (name: string, value: string) => {
    try {
      localStorage.setItem(name, value);
    } catch {
      // ignore
    }
  },
  removeItem: (name: string) => {
    try {
      localStorage.removeItem(name);
    } catch {
      // ignore
    }
  },
};

export const useClassStore = create<ClassState>()(
  persist(
    (set) => ({
      currentClass: null,
      setCurrentClass: (cls) => set((state) => {
        const countOf = (c: Class | null) => {
          if (!c) return null;
          if (typeof c.studentCount === 'number') return c.studentCount;
          return c.students?.length ?? 0;
        };
        // Prevent unnecessary updates if the object is effectively the same
        if (state.currentClass === cls) return {};
        if (state.currentClass && cls &&
          state.currentClass.id === cls.id &&
          state.currentClass.name === cls.name &&
          state.currentClass.isSoundEnabled === cls.isSoundEnabled &&
          state.currentClass.isAttenuationEnabled === cls.isAttenuationEnabled &&
          state.currentClass.lockScreenPassword === cls.lockScreenPassword &&
          state.currentClass.parentShareEnabled === cls.parentShareEnabled &&
          state.currentClass.parentShareToken === cls.parentShareToken &&
          state.currentClass.allowMultipleGroups === cls.allowMultipleGroups &&
          countOf(state.currentClass) === countOf(cls)) {
          return {};
        }
        return { currentClass: cls };
      }),
    }),
    {
      name: 'class-storage',
      storage: createJSONStorage(() => safeLocalStorage),
    }
  )
);
